<template lang="pug">

    div

        //- Lieferadresse
        address-dropdown-field(
            label='Lieferadresse'
            optionLabel='name'
            optionValue='id'
            v-bind:address-models='addressModelOptions'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:force-shipping-address='true'
            v-bind:person-models='personModels'
            v-bind:required='true'
            v-model='addressId'
        )
            error(:$v='$v.addressId' validator='required')
                | Darf nicht leer sein. Bitte eine Lieferadresse auswählen

        // Ansprechpartner
        text-field(
            label='Ansprechpartner (der Lieferadresse)'
            v-bind:isReadOnly='true'
            v-model='defaultContactName'
            placeholder='nicht vorhanden'
            v-show="addressModel.id"
        )

        //- Abweichender Ansprechpartner
        person-dropdown-field(
            label="Abweichender Ansprechpartner"
            v-bind:company-model="contactCompanyModel"
            v-bind:person-models="contactPersonModels"
            v-model='contactId'
            v-show="addressModel.id"
        )

        // Anzahl
        number-field(label='Anzahl' v-model='amount')

        // Lieferbedingungen
        textarea-field(label='Lieferbedingungen' v-model='deliveryConditions')


</template>

<script>
    import * as R from 'ramda'
    import {
        find_default_shipping_address,
        format_name_by_default_shipping_address,
        sort_by_is_default_shipping_address,
    } from '@/utils/address'
    import { full_name } from '@/utils/person'
    import { find_by_default_shipping_address } from '@/utils/company'
    import { required } from 'vuelidate/lib/validators'
    import AddressDropdownField from '@/components/addresses/DropdownField'
    import Error from '@/components/form/FieldError.vue'
    import NumberField from '@/components/form/NumberField.vue'
    import PersonDropdownField from '@/components/persons/DropdownField'
    import TextareaField from '@/components/form/TextareaField.vue'
    import TextField from '@/components/form/TextField.vue'

    //noinspection JSUnusedGlobalSymbols
    export default {

        components: { AddressDropdownField, Error, NumberField, PersonDropdownField, TextareaField, TextField },

        computed: {

            $invalid() {
                return this.$v.$invalid
            },

            addressId: {
                get() { return this.value.address },
                set(value) {
                    this.$emit('input', {
                        ...this.value,
                        address: value,
                        contact: null,
                    })
                }
            },

            addressModel() {
                return this.$store.getters['addresses/byId'](this.addressId) || {}
            },

            addressModelOptions() {
                return R.compose(

                    // Name der Standardlieferadressen markieren
                    R.map(format_name_by_default_shipping_address),

                    // Nach Standardlieferadresse sortieren
                    sort_by_is_default_shipping_address

                )(this.addressModels)
            },

            amount: {
                get() { return this.value.amount },
                set(value) { this.updateValue('amount', value) }
            },

            availableContactModels() {
                return this.personModels.map(personModel => ({ ...personModel, fullName: full_name(personModel) }))
            },

            contactId: {
                get() { return this.value.contact || 0 },
                set(value) { this.updateValue('contact', value) }
            },

            contactCompanyId() {
                const linkedCompanies = (this.addressModel.companies || [])
                    .map(link => this.$store.getters['companies/byId'](link.company))
                    .filter(Boolean)

                const owner = find_by_default_shipping_address(linkedCompanies, this.addressId)

                return owner ? owner.id : this.companyModel.id
            },

            contactCompanyModel() {
                if (this.contactCompanyId === this.companyModel.id) {
                    return this.companyModel
                }
                return this.$store.getters['companies/byId'](this.contactCompanyId) || {}
            },

            contactPersonModels() {
                if (this.contactCompanyId === this.companyModel.id) {
                    return this.personModels
                }
                return this.$store.getters['persons/allByCompanyId'](this.contactCompanyId) || []
            },

            defaultContactName() {
                return this.addressModel.contactName || null
            },

            deliveryConditions: {
                get() { return this.value.deliveryConditions },
                set(value) { this.updateValue('deliveryConditions', value) }
            },

        },

        created() {
            // ggf. Adresse vorauswaehlen
            if (this.enablePreselection && R.isNil(this.addressId)) {
                this.updateValue('address', find_default_shipping_address(this.addressModels).id)
            }
        },

        watch: {
            addressId(value) {
                if (value == null) return

                // Lieferbedingungen mit dem Versandhinweis des aktuellen
                // Unternehmens für diese Adresse vorbelegen
                const address = this.$store.getters['addresses/byId'](value) || {}
                const link = (address.companies || []).find(link => link.company === this.companyModel.id)
                this.updateValue('deliveryConditions', link ? (link.notice || '') : '')

                // Load the companies linked to the address so the owner company
                // (the one having this address as default shipping address) can be resolved.
                const linkedCompanies = address.companies || []
                linkedCompanies.forEach(link => {
                    if (link.company !== this.companyModel.id) {
                        this.$store.dispatch('companies/loadOne', { id: link.company })
                    }
                })
            },

            contactCompanyId(companyId) {
                // Load the shipping address owner company and its persons
                // unless it is the purchaser (already loaded in Step5).
                if (companyId == null || companyId === this.companyModel.id) return

                this.$store.dispatch('companies/loadOne', { id: companyId })
                this.$store.dispatch('persons/loadAllByCompanyId', { companyId })
            }
        },

        methods: {

            $touch() {
                this.$v.$touch()
            },

            updateValue(prop, value) {
                this.$emit('input', R.assoc(prop, value, this.value))
            },

        },

        props: {
            addressModels: {
                type: Array,
                required: true
            },
            companyModel: {
                type: Object,
                required: true
            },
            countryModels: {
                type: Array,
                required: true
            },
            enablePreselection: {
                type: Boolean,
                default: false
            },
            personModels: {
                type: Array,
                required: true
            },
            value: {
                type: Object,
                required: true
            }
        },

        validations: {
            addressId: { required }
        },

    }

</script>
